/**
 * A form filled many times in one episode, shown as the paper shows it: the Labour Care Guide grid
 * (parameters down the side, the alert threshold next to each, one column per assessment), with
 * every value that meets the threshold marked. Under it, what the latest assessment set off: the
 * alerts, and the risk estimates, re-evaluated on this device from the record.
 */
import {
  type ConceptDef,
  type FormDef,
  type FormValues,
  type Pack,
  type Patient,
  type PatientRecord,
  buildContext,
  ecClock,
  evaluatePack,
  formOfEncounter,
  labourHour,
  seriesGrid,
} from '@assist/core';
import { clock } from '../controller.js';
import { t } from '../i18n.js';
import { RiskCard } from './CdsPanel.js';

// shared with the OpenMRS module (core/series.ts); kept here for existing imports
export { ecClock, thresholdLabel } from '@assist/core';

const shortValue = (c: ConceptDef, v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? t.yes : t.no;
  if (c.type === 'datetime' && typeof v === 'string') return ecClock(v);
  return String(v);
};

export function SeriesCard(props: {
  pack: Pack;
  form: FormDef & { series: NonNullable<FormDef['series']> };
  patient: Patient;
  record: PatientRecord;
  allowDraft: boolean;
}) {
  const { pack, form, record } = props;
  const now = clock.now();
  const grid = seriesGrid(pack, form, record, now, formOfEncounter);
  const cols = grid.cols;
  if (!cols.length) return null;

  // The latest assessment, evaluated as it was when it was recorded.
  const last = cols[cols.length - 1];
  let result = null as ReturnType<typeof evaluatePack> | null;
  const rulesHidden = !props.allowDraft && !pack.rules.some((r) => r.status === 'approved');
  if (last && !rulesHidden) {
    const formValues: FormValues = Object.fromEntries(last.values);
    const ctx = buildContext(pack, {
      today: last.at.slice(0, 10),
      now: last.at,
      patient: props.patient,
      episodes: record.episodes,
      observations: record.observations.filter((o) => !last.obsIds.has(o.id)),
      medications: record.medications,
      form: formValues,
    });
    result = evaluatePack(pack, ctx, { allowDraft: props.allowDraft, form: form.id });
  }
  const alerts = (result?.outputs ?? []).filter(
    (o) =>
      o.action !== 'predict' &&
      o.action !== 'classify' &&
      o.action !== 'enrol' &&
      o.severity !== 'info',
  );
  const predictions = (result?.outputs ?? []).flatMap((o) => (o.prediction ? [o.prediction] : []));

  return (
    <div class="card" data-card="series" data-form={form.id}>
      <h3>
        {form.series.title}
        <span class="muted">{t.assessmentsN(cols.length)}</span>
      </h3>
      {alerts.length ? (
        <div class="alert urgent" role="alert">
          <p>
            <strong>{t.latestAlerts(alerts.length)}</strong>
          </p>
          <ul class="why lcg-alerts">
            {alerts.map((o) => (
              <li key={o.ruleId} data-rule={o.ruleId}>
                <strong>{o.title}.</strong> {o.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {predictions.length ? (
        <div class="risk-grid">
          {predictions.map((p) => (
            <RiskCard key={p.modelId} p={p} />
          ))}
        </div>
      ) : null}
      <div class="lcgwrap">
        <table class="lcg">
          <thead>
            <tr>
              <th class="lcgp">{t.observation}</th>
              <th class="lcga">{t.alertCol}</th>
              {cols.map((c) => (
                <th key={c.enc}>
                  {ecClock(c.at)}
                  <div class="muted lcgh">
                    {labourHour(grid, c.at) !== null ? `h${labourHour(grid, c.at)}` : t.latent}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.sections.map((s) => [
              <tr class="lcgg" key={s.title}>
                <td colSpan={cols.length + 2}>{s.title}</td>
              </tr>,
              ...s.rows.map((row) => (
                <tr key={row.concept.code}>
                  <td class="lcgp">{row.concept.display}</td>
                  <td class="lcga">{row.threshold}</td>
                  {row.cells.map((cell, i) =>
                    cell.value === undefined ? (
                      <td key={cols[i]?.enc} class="lcgc muted">
                        ·
                      </td>
                    ) : (
                      <td
                        key={cols[i]?.enc}
                        class={cell.alert ? 'lcgc lcgalert' : 'lcgc'}
                        data-concept={row.concept.code}
                      >
                        {shortValue(row.concept, cell.value)}
                      </td>
                    ),
                  )}
                </tr>
              )),
            ])}
          </tbody>
        </table>
      </div>
    </div>
  );
}
